import { useState } from "react"
import { DateTime } from "luxon"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, InputAdornment, MenuItem, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { FormikDropdown, FormikNumberField, FormikSwitch } from "../../../../../shared/components/forms/Formik"
import { ConfigureLanguage, CreateOrEditShopFormValues, ShopTypes } from "../shopsTypes"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { DeadlineField, dayOptions } from "./formElements/DeadlineField"
import { iterateStringEnum } from "../../../../../lib/enums"
import { UserType } from "../../../../login/loginTypes"
import { MatchActualUserType } from "../../../../accessControl/MatchActualUserType"

const useStyles = makeStyles()((_theme) => ({
    switchLabelWrap: {
        "& label.MuiFormControlLabel-root": {
            margin: 0,
            justifyContent: "flex-start",
        },
    },
    boxColl: {
        maxWidth: "20rem",
    },
}))
export const FormShopContentSettings = () => {
    const { classes } = useStyles()
    const defaultTime = DateTime.fromISO("12:00")
    const { values, setFieldValue } = useFormikContext<CreateOrEditShopFormValues>()
    const [checkedDeadline, setCheckedDeadline] = useState(!!values?.deadline)

    const millisecondsAdornment = (
        <InputAdornment position="end">
            <LocalizedStrict id="merchant-shop-form-posDetails-configuration-for-supporters-ms">
                <span>ms</span>
            </LocalizedStrict>
        </InputAdornment>
    )

    const handleChangeIsPublicShop = (checked: boolean) => {
        setCheckedDeadline(checked)
        if (checked) {
            setFieldValue("deadline.purchases.daysBefore", dayOptions[0].value)
            setFieldValue("deadline.purchases.timeOfDay", defaultTime)
            setFieldValue("deadline.cancellation.daysBefore", dayOptions[0].value)
            setFieldValue("deadline.cancellation.timeOfDay", defaultTime)
        } else setFieldValue("deadline", null)
    }
    return (
        <StandardForm>
            {values.type === ShopTypes.POS && (
                <StandardMainPanel>
                    <LocalizedStrict
                        id="merchant-shop-form-posDetails-defaultLanguage-field-label"
                        attrs={{ label: true, helperText: true }}
                    >
                        <FormikDropdown name="posDetails.defaultLanguage" label="Default language">
                            {[...iterateStringEnum(ConfigureLanguage)].map((code) => (
                                <MenuItem key={code} value={code}>
                                    <LocalizedEnum
                                        base="merchant-shop-form-posDetails-defaultLanguage-lang"
                                        enumValue={code}
                                    >
                                        <span>{code}</span>
                                    </LocalizedEnum>
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                </StandardMainPanel>
            )}
            <StandardMainPanel>
                <LocalizedStrict id="merchant-shop-form-check-in">
                    <Typography>Check in</Typography>
                </LocalizedStrict>
                <Box className={classes.switchLabelWrap}>
                    <LocalizedStrict id="merchant-shop-form-check-in-qr-code-on-receipt">
                        <FormikSwitch name="shopDetails.settings.showQRReceipt" labelPlacement="end">
                            Show check in QR code on receipt
                        </FormikSwitch>
                    </LocalizedStrict>
                </Box>
                <Box className={classes.switchLabelWrap}>
                    <LocalizedStrict id="merchant-shop-form-accept-multiple-check-ins">
                        <FormikSwitch name="shopDetails.settings.acceptMultipleCheckIns" labelPlacement="end">
                            Accept multiple check ins
                        </FormikSwitch>
                    </LocalizedStrict>
                </Box>
            </StandardMainPanel>
            <LocalizedStrict id="merchant-shop-form-deadline" attrs={{ title: true, offContent: true }}>
                <StandardMainPanel
                    variant="switchable"
                    title="Deadline"
                    checked={checkedDeadline}
                    onCheckChange={handleChangeIsPublicShop}
                    offContent="Deadline not activated"
                >
                    <DeadlineField />
                </StandardMainPanel>
            </LocalizedStrict>
            {values.type === ShopTypes.POS && (
                <MatchActualUserType userTypes={[UserType.SUPPORT]}>
                    <LocalizedStrict
                        id="merchant-shop-form-posDetails-configuration-for-supporters"
                        attrs={{ title: true }}
                    >
                        <StandardMainPanel variant="collapsible" title="Configuration for supporters">
                            <LocalizedStrict
                                id="merchant-shop-form-posDetails-configuration-for-supporters-returnToHomeScreenDelay-field-label"
                                attrs={{ label: true }}
                            >
                                <FormikNumberField
                                    label="Return to home screen delay"
                                    name="posDetails.returnToHomeScreenDelay"
                                    range="non-negative-integer"
                                    InputProps={{ endAdornment: millisecondsAdornment }}
                                    className={classes.boxColl}
                                />
                            </LocalizedStrict>
                            <LocalizedStrict
                                id="merchant-shop-form-posDetails-configuration-for-supporters-returnToHomeAfterPurchase-field-label"
                                attrs={{ label: true }}
                            >
                                <FormikNumberField
                                    label="Return to home after purchase"
                                    name="posDetails.returnToHomeAfterPurchase"
                                    range="non-negative-integer"
                                    InputProps={{ endAdornment: millisecondsAdornment }}
                                    className={classes.boxColl}
                                />
                            </LocalizedStrict>
                        </StandardMainPanel>
                    </LocalizedStrict>
                </MatchActualUserType>
            )}
        </StandardForm>
    )
}
